import * as routes from "../../constants/routes";
import ActionButton from "../ActionButton";
import BackButton from "../BackButton";
import Checkbox from "../Checkbox";
import ElectronicCommunicationsAgreementModal from "../ElectronicCommunicationsAgreementModal";
import InterestTable from "../InterestTable";
import React, {useState} from "react";
import ScrollLock from "../ScrollLock";
import styles from "./DisclosureInfo.module.css";
import {Container} from "../grid";
import CheckboxEnhanced from "../ CheckboxEnhanced";
import cn from "../../lib/classnames";
import callApplicationLead from "../../lib/callApplicationLead";
import {ALABAMA, NEBRASKA} from "../ExpandableAddress";

const strings = {
    authorization:
        "Authorization for the Social Security Administration to Disclose Your Social Security Number Verification.",
    authorization2:
        "I authorize the Social Security Administration (SSA) to verify and disclose to 1st Financial Bank USA, Student Center, P.O. Box 1050, N. Sioux City, SD 57049 and SentiLink, as its service provider, for the purpose of this transaction whether the name, Social Security Number (SSN) and date of birth I have submitted matches information in SSA records. My consent is for a one-time validation within the next 60 days.",
    confirm: "I confirm that I have read and agree to the important information above.",
    consent:
        "I consent to allow 1st Financial Bank USA to consider me for all credit card product offers.",
    signing:
        "I am signing the consent for SSA to disclose my SSN Verification to 1st Financial Bank USA and SentiLink. I agree that my electronic signature has the same legal meaning, validity, and effect as my handwritten signature.",
    submit: "Submit Application",
    pleaseRead: "Please read and agree to important information and disclosures",
    agreeToInfoText: 'By checking this box and clicking "Submit Application" below:',
    agreeToElectronicText:
        "By checking this box, you agree that we may provide your application decision, and if approved, your 1st Financial Bank USA card account disclosures and other communications electronically rather than by paper. You have the right to receive the information in paper and may withdraw your agreement at any time. Click Electronic Communications Agreement for more information, including how to access electronic communications, withdraw your consent, update your email address, or request a paper copy of information provided electronically.",
    electronicAgreementLink: "Electronic Communications Agreement",
    electronicAgreementBoldLabel: "Electronic Communications -  ",
    alabamaStateAgreement:
        "I affirm that I am at least 19 years old; or pursuant to Ala. Code 26-1-1(f), if I am 18 years old, then I am of sound mind to enter into this binding contract. I understand that my age does not allow me to rescind, avoid, or repudiate this contract or any rights or privileges under this contract.",
    nebraskaStateAgreement:
        "I affirm that I am at least 19 years old; or pursuant to Neb. Rev. Stat. § 43-2101, if I am 18 years old, then I am not a ward of the state, which authorizes me to enter into this binding contract. I understand that my age does not allow me to rescind, avoid, or repudiate this contract or any rights or privileges under this contract.",
};

export const INTEREST_APR = 33.65;
export const ANNUAL_FEE = 55;

type Props = {
    onSubmit: (
        agreeToElectronicCommunications: boolean,
        ECBSVConsent: boolean,
        nscConsent: boolean,
    ) => void;
    loading: boolean;
    goBack?: () => void;
    applicationAddressState: string;
};

export default function DisclosureInfo(props: Props) {
    const [ECBSVConsent, setECBSVConsent] = useState(false);
    const [agreeToElectronic, setAgreeToElectronic] = useState(false);
    const [agreeToStateAgreement, setAgreeToStateAgreement] = useState(false);
    const [confirmationModal, setConfirmationModal] = useState(false);

    function closeConfirmationModal() {
        setConfirmationModal(false);
    }

    const stateAgreementRequired =
        props.applicationAddressState === ALABAMA || props.applicationAddressState === NEBRASKA;

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!ECBSVConsent || (stateAgreementRequired && !agreeToStateAgreement)) {
            return;
        }

        if (agreeToElectronic) {
            props.onSubmit(agreeToElectronic, ECBSVConsent, ECBSVConsent);
        } else {
            setConfirmationModal(true);
        }
    }

    const submitButtonRef = React.useRef<HTMLButtonElement>(null);

    return (
        <>
            <form onSubmit={onSubmit}>
                <Container>
                    <div className={styles.container}>
                        {props.goBack ? (
                            <BackButton
                                className={styles.back}
                                disabled={props.loading}
                                onClick={props.goBack}
                            />
                        ) : null}
                        <div className={styles.agreementText}>{strings.pleaseRead}</div>
                    </div>

                    <ScrollLock>
                        <div className={styles.table}>
                            <InterestTable apr={INTEREST_APR} annualFee={ANNUAL_FEE} />
                        </div>
                    </ScrollLock>

                    {stateAgreementRequired ? (
                        <div className={styles.checkboxContainer}>
                            <CheckboxEnhanced
                                className={styles.checkbox}
                                id="checkbox-state-specific"
                                label={strings.agreeToInfoText}
                                isChecked={agreeToStateAgreement}
                                onChange={(e) => {
                                    setAgreeToStateAgreement(e.target.checked);
                                }}
                                hasGradient={true}
                                required={true}
                            >
                                <ul>
                                    <li className={styles.listItem}>
                                        {props.applicationAddressState === ALABAMA
                                            ? strings.alabamaStateAgreement
                                            : null}
                                        {props.applicationAddressState === NEBRASKA
                                            ? strings.nebraskaStateAgreement
                                            : null}
                                    </li>
                                </ul>
                            </CheckboxEnhanced>
                        </div>
                    ) : null}
                    <div className={styles.checkboxContainer}>
                        <CheckboxEnhanced
                            className={styles.checkbox}
                            id="checkbox-1"
                            label={strings.agreeToInfoText}
                            isChecked={ECBSVConsent}
                            onChange={(e) => {
                                setECBSVConsent(e.target.checked);
                                callApplicationLead(
                                    "direct-form-ecbsv-checkbox",
                                    null,
                                    window.location.href,
                                    e.target.checked.toString(),
                                );
                            }}
                            hasGradient={true}
                            required={true}
                        >
                            <ul>
                                <li className={styles.listItem}>{strings.confirm}</li>
                                <li className={styles.listItem}>{strings.consent}</li>
                                <li>
                                    <div className={cn(styles.bold, styles.listItem)}>
                                        {strings.authorization}
                                    </div>
                                    <li className={cn(styles.listItem, styles.indent)}>
                                        {strings.authorization2}
                                    </li>
                                    <li className={cn(styles.listItem, styles.indent)}>
                                        {strings.signing}
                                    </li>
                                </li>
                            </ul>
                        </CheckboxEnhanced>
                    </div>
                    <div className={styles.checkboxContainer}>
                        <Checkbox
                            className={styles.checkbox}
                            id="checkbox-2"
                            label={strings.agreeToElectronicText}
                            isChecked={agreeToElectronic}
                            onChange={(e) => setAgreeToElectronic(e.target.checked)}
                            hasGradient={true}
                            linkText={strings.electronicAgreementLink}
                            to={routes.ELECTRONIC_AGREEMENT_FULL}
                            optionalFrontBoldLabel={strings.electronicAgreementBoldLabel}
                        />
                    </div>
                    <div className={styles.footer}>
                        <ActionButton
                            label={strings.submit}
                            ref={submitButtonRef}
                            loading={props.loading}
                            disabled={
                                !ECBSVConsent || (stateAgreementRequired && !agreeToStateAgreement)
                            }
                        />
                    </div>
                </Container>
            </form>
            <ElectronicCommunicationsAgreementModal
                isOpen={confirmationModal}
                onClose={closeConfirmationModal}
                onSubmit={(e) => {
                    props.onSubmit(e, ECBSVConsent, ECBSVConsent);
                    closeConfirmationModal();
                }}
                triggerElement={submitButtonRef.current}
            />
        </>
    );
}
